import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { Users } from 'lucide-react'
import { type Candidate } from 'lib/api'

interface CreateGroupFromCandidatesModalProps {
  selectedCandidateIds: number[]
  availableCandidates: Candidate[]
  onCreateGroup: (groupData: { name: string; description?: string; color: string }) => Promise<void>
  onCancel: () => void
  loading: boolean
}

export function CreateGroupFromCandidatesModal({
  selectedCandidateIds,
  availableCandidates,
  onCreateGroup,
  onCancel,
  loading
}: CreateGroupFromCandidatesModalProps) {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#007bff'
  })

  const selectedCandidates = availableCandidates.filter(c => selectedCandidateIds.includes(c.id))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim()) return
    await onCreateGroup(formData)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="text-sm font-medium">{t('teams.fields.name', 'Group Name')}</label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          placeholder={t('teams.placeholders.name', 'Enter group name')}
          className="mt-1"
          required
          disabled={loading}
        />
      </div>
      
      <div>
        <label className="text-sm font-medium">{t('teams.fields.description', 'Description')}</label>
        <Input
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder={t('teams.placeholders.description', 'Enter group description (optional)')}
          className="mt-1"
          disabled={loading}
        />
      </div>
      
      {/* Color Selection */}
      <div>
        <label className="text-sm font-medium mb-2 block">{t('teams.fields.color', 'Group Color')}</label>
        <div className="flex items-center space-x-2">
          {[
            '#007bff', '#28a745', '#dc3545', '#ffc107', 
            '#6f42c1', '#fd7e14', '#20c997', '#e83e8c'
          ].map((color) => (
            <button
              key={color}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, color }))}
              disabled={loading}
              className={`w-6 h-6 rounded-full border-2 transition-all hover:scale-110 ${
                formData.color === color ? 'border-gray-800 dark:border-gray-200 shadow-lg' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              } ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              style={{ backgroundColor: color }}
              title={t('teams.colorSwatch', 'Color')}
            />
          ))}
          
          {/* Custom Color */}
          <div className="relative">
            <input
              type="color"
              value={formData.color}
              onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
              disabled={loading}
              className="w-6 h-6 rounded-full border-2 border-gray-200 dark:border-gray-700 cursor-pointer opacity-0 absolute"
              style={{ 
                WebkitAppearance: 'none',
                appearance: 'none'
              }}
            />
            <div 
              className={`w-6 h-6 rounded-full border-2 transition-all hover:scale-110 ${
                !['#007bff', '#28a745', '#dc3545', '#ffc107', 
                  '#6f42c1', '#fd7e14', '#20c997', '#e83e8c'].includes(formData.color)
                  ? 'border-gray-800 dark:border-gray-200 shadow-lg' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              } ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              style={{ backgroundColor: formData.color }}
              title={t('teams.customColor', 'Custom Color')}
            />
          </div>
        </div>
      </div>

      {/* Selected Candidates Preview */}
      <div>
        <label className="text-sm font-medium mb-2 block">
          {t('offerDetail.selectedCandidatesPreview', 'Selected Candidates')} ({selectedCandidates.length})
        </label>
        <div className="max-h-32 overflow-y-auto border rounded-lg p-2 space-y-1 bg-muted/50">
          {selectedCandidates.map((candidate) => (
            <div key={candidate.id} className="text-sm text-muted-foreground">
              • {candidate.name} {candidate.email && `(${candidate.email})`}
            </div>
          ))}
        </div>
      </div>
      
      <div className="flex justify-end space-x-2 pt-4">
        <Button 
          type="button"
          variant="outline" 
          onClick={onCancel}
          disabled={loading}
        >
          {t('common.cancel')}
        </Button>
        <Button 
          type="submit"
          disabled={!formData.name.trim() || loading}
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {t('common.creating', 'Creating...')}
            </>
          ) : (
            <>
              <Users className="h-4 w-4 mr-2" />
              {t('offerDetail.createGroupAndAdd', 'Create Group & Add Candidates')}
            </>
          )}
        </Button>
      </div>
    </form>
  )
}

